import * as Sentry from '@sentry/react';

/**
 * Client-side error monitoring.
 *
 * The Sentry DSN is a PUBLIC, write-only ingest key — Sentry's own docs say it
 * is safe to ship in the browser bundle (it can only *send* events, never read
 * project data). So the prod DSN is baked in as a default; `VITE_SENTRY_DSN`
 * overrides it (e.g. to aim a staging build at a different project).
 *
 * Only real production builds report. `vite dev` and mock previews
 * (`?mock=full`, which is dev-only) leave Sentry off, so the dashboard never
 * fills with noise from developer machines.
 */
const DEFAULT_DSN =
  'https://d1177239716bfab8d49375ea3be18717@o4511483970256896.ingest.de.sentry.io/4511484035530832';

export function initSentry(): void {
  if (!import.meta.env.PROD) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN || DEFAULT_DSN;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Closed community — never ship user IPs / PII to a third party.
    sendDefaultPii: false,
    // Errors only for now (no performance tracing / session replay), which
    // also keeps the runtime overhead and bundle weight minimal.
    tracesSampleRate: 0,
    // A browser quirk, not an app bug — Chrome fires this when a ResizeObserver
    // callback can't settle in one frame. Filtering it keeps the inbox signal.
    ignoreErrors: ['ResizeObserver loop limit exceeded', 'ResizeObserver loop completed'],
  });
}

export type AppErrorKind = 'chunk' | 'network' | 'runtime';

/** Report a top-level renderer failure with enough context to reproduce it. */
export function captureAppError(
  error: Error,
  context: { incidentId: string; kind: AppErrorKind; componentStack?: string | null },
): void {
  // Keep dev/mock sessions out of Sentry, matching initSentry(). Console output
  // remains available through the boundary itself.
  if (!import.meta.env.PROD) return;
  Sentry.withScope((scope) => {
    scope.setTag('incident_id', context.incidentId);
    scope.setTag('error_kind', context.kind);
    scope.setTag('desktop_shell', window.bunkerDesktop?.isDesktop === true ? 'yes' : 'no');
    scope.setContext('navigation', {
      path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      online: navigator.onLine,
    });
    if (context.componentStack) scope.setExtra('component_stack', context.componentStack);
    Sentry.captureException(error);
  });
}
